import { createContext, useContext, useState, useEffect, useRef } from 'react';
import JsSIP from 'jssip';

// Crear el Context
const SoftphoneContext = createContext();

// Provider
export function SoftphoneProvider({ children }) {
    const [sipUser, setSipUser] = useState('');
    const [sipPass, setSipPass] = useState('');
    const [regStatus, setRegStatus] = useState('');
    const [regStatusColor, setRegStatusColor] = useState('gray');
    const [callStatus, setCallStatus] = useState('');
    const [callStatusColor, setCallStatusColor] = useState('gray');
    const [destinoCliente, setDestinoCliente] = useState('');
    const [destinoEjecutivo, setDestinoEjecutivo] = useState('');
    const [isRegistered, setIsRegistered] = useState(false);
    const [incomingSession, setIncomingSession] = useState(null);
    const [showIncomingCall, setShowIncomingCall] = useState(false);
    const [softphoneConnectionStatus, setSoftphoneConnectionStatus] = useState('disconnected');
    const [softphoneSessionDirection, setSoftphoneSessionDirection] = useState('none'); 
    const [outgoingCallStatus, setOutgoingCallStatus] = useState('none'); 
    const [incomingCallStatus, setIncomingCallStatus] = useState('none'); 
    const [executiveCallStatus, setExecutiveCallStatus] = useState('none'); 
    const [muted, setMuted] = useState(false);
    const [holded, setHolded] = useState(false);


    const uaRef = useRef(null);
    const sessionClienteRef = useRef(null);
    const sessionEjecutivoRef = useRef(null);
    const audioContextRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const ringtoneOscillatorRef = useRef(null);
    const ringtoneIntervalRef = useRef(null);


    useEffect(() => {
        let sessionData = JSON.parse(sessionStorage.getItem('sessionData'));
        if (sessionData && sessionData.extension && softphoneConnectionStatus === 'disconnected'){
            console.log('Reiniciando sesión softphone:', sessionData.extension);
            register(String(sessionData.extension), process.env.REACT_APP_ASTERISK_PASSWORD);
        }

        setDestinoEjecutivo(process.env.REACT_APP_CLIENT_REFER_CALLS_NUMBER || '');
    }, []);

    const attachMedia = (session) => {
        const pc = session.connection;

        pc.ontrack = (e) => {
            const remoteStream = new MediaStream();
            e.streams[0].getTracks().forEach(track => {
                remoteStream.addTrack(track);
            });
            if (remoteAudioRef.current) {
                remoteAudioRef.current.srcObject = remoteStream;
            }
            console.log('Audio remoto conectado');
        };

        const remoteStream = new MediaStream();
        pc.getReceivers().forEach(r => {
            if (r.track) remoteStream.addTrack(r.track);
        });
        if (remoteStream.getTracks().length > 0 && remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = remoteStream;
        }
    };


    const playRingtone = () => {
        try {
            // Inicializar AudioContext si no existe
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }

            const context = audioContextRef.current;

            // Detener tono anterior si existe
            stopRingtone();

            // Crear oscilador para el tono
            const oscillator = context.createOscillator();
            const gainNode = context.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(context.destination);

            // Configurar tono (frecuencias típicas de teléfono: 440Hz y 480Hz alternados)
            oscillator.type = 'sine';
            oscillator.frequency.value = 440;

            // Volumen
            gainNode.gain.value = 0.3;

            // Efecto de ring (alternar frecuencia)
            let toggle = true;
            const interval = setInterval(() => {
                if (oscillator.frequency) {
                    oscillator.frequency.value = toggle ? 0 : 440;
                    toggle = !toggle;
                }
            }, 1000);

            ringtoneIntervalRef.current = interval;
            oscillator.onended = () => {
                if (ringtoneIntervalRef.current) {
                    clearInterval(ringtoneIntervalRef.current);
                    ringtoneIntervalRef.current = null;
                }
            };

            oscillator.start();
            ringtoneOscillatorRef.current = { type: 'simple', oscillator };

        } catch (err) {
            console.log('Error reproduciendo ringtone:', err);
        }
    };

    // Reproducir tono de teléfono clásico (estilo antiguo)
    const playIncomingRingtone = () => {
        try {
            // Inicializar AudioContext si no existe
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }

            const context = audioContextRef.current;

            // Detener tono anterior si existe
            stopRingtone();

            // Crear dos osciladores para el tono dual clásico (ring-ring)
            const osc1 = context.createOscillator();
            const osc2 = context.createOscillator();
            const gainNode = context.createGain();

            osc1.connect(gainNode);
            osc2.connect(gainNode);
            gainNode.connect(context.destination);

            // Frecuencias del ringtone clásico (440Hz + 480Hz simultáneos)
            osc1.type = 'sine';
            osc2.type = 'sine';
            osc1.frequency.value = 440;
            osc2.frequency.value = 480;

            // Volumen inicial
            gainNode.gain.value = 0;

            // Patrón clásico: Ring (2s) - Silencio (4s) - Ring (2s) - Silencio (4s)
            const now = context.currentTime;
            const ringDuration = 2.0;
            const pauseDuration = 4.0;
            const cycleTime = ringDuration + pauseDuration;

            let time = now;

            // Programar el patrón de ring repetitivo
            const scheduleRing = () => {
                // Ring ON
                gainNode.gain.setValueAtTime(0, time);
                gainNode.gain.linearRampToValueAtTime(0.3, time + 0.05); // Fade in rápido
                time += ringDuration;

                // Ring OFF
                gainNode.gain.setValueAtTime(0.3, time);
                gainNode.gain.linearRampToValueAtTime(0, time + 0.05); // Fade out rápido
                time += pauseDuration;
            };

            // Programar varios ciclos
            for (let i = 0; i < 10; i++) {
                scheduleRing();
            }

            osc1.start(now);
            osc2.start(now);
            osc1.stop(now + (cycleTime * 10));
            osc2.stop(now + (cycleTime * 10));

            // Guardar referencia para poder detenerlo
            ringtoneOscillatorRef.current = { type: 'classic', osc1, osc2 };

        } catch (err) {
            console.log('Error reproduciendo ringtone clásico:', err);
        }
    };

    const stopRingtone = () => {
        // Limpiar intervalo si existe
        if (ringtoneIntervalRef.current) {
            clearInterval(ringtoneIntervalRef.current);
            ringtoneIntervalRef.current = null;
        }

        // Detener osciladores
        if (ringtoneOscillatorRef.current) {
            try {
                if (ringtoneOscillatorRef.current.type === 'classic') {
                    // Ringtone clásico (dos osciladores)
                    ringtoneOscillatorRef.current.osc1.stop();
                    ringtoneOscillatorRef.current.osc2.stop();
                } else if (ringtoneOscillatorRef.current.type === 'simple') {
                    // Ringtone simple (un oscilador)
                    ringtoneOscillatorRef.current.oscillator.stop();
                }
                ringtoneOscillatorRef.current = null;
            } catch (err) {
                // El oscilador ya se detuvo, ignorar el error
                ringtoneOscillatorRef.current = null;
            }
        }
    };

    const register = (sipUser, sipPass) => {

       //console.log('debug connection params---->', sipUser, sipPass);
        setSoftphoneConnectionStatus('connecting');

        if (!sipUser.trim() || !sipPass.trim()) {
            alert('Ingresa usuario y contraseña');
            return;
        }

        //const socket = new JsSIP.WebSocketInterface('wss://pbxcloud.athenacampaign.com:8089/ws');
        const wsiURL= `${process.env.REACT_APP_ASTERISK_SOCKET_PROTOCOL}://${process.env.REACT_APP_ASTERISK_SERVER}:${process.env.REACT_APP_ASTERISK_PORT}/ws`;
        console.log('WebSocket URL:', wsiURL);
        const socket = new JsSIP.WebSocketInterface(wsiURL);
        const configuration = {
            sockets: [socket],
            //uri: `sip:${sipUser}@pbxcloud.athenacampaign.com`,
            uri: `sip:${sipUser}@${process.env.REACT_APP_ASTERISK_SERVER}`,
            authorization_user: sipUser,
            password: sipPass,
            display_name: sipUser,
            session_timers: false
        };

        console.log('Configuración SIP:', configuration);

        uaRef.current = new JsSIP.UA(configuration);
        uaRef.current.start();

        uaRef.current.on('registered', () => {
            setRegStatus('✅ Registrado correctamente');
            setRegStatusColor('green');
            setIsRegistered(true);
            setSoftphoneConnectionStatus('connected');
            console.log('Usuario registrado:', sipUser);
        });

        uaRef.current.on('unregistered', () => {
            setRegStatus('❌ No registrado');
            setRegStatusColor('red');
            setIsRegistered(false);
            setSoftphoneConnectionStatus('disconnected');
        });

        uaRef.current.on('registrationFailed', (e) => {
            setRegStatus('❌ Error de registro');
            setRegStatusColor('red');
            setIsRegistered(false);
            console.error('Registration failed:', e);
            setSoftphoneConnectionStatus('disconnected');
        });

        uaRef.current.on('newRTCSession', (e) => {
            const session = e.session;

            setSoftphoneSessionDirection(session.direction);

            if (session.direction === 'incoming') {

                console.log('Llamada entrante de:', session.remote_identity.uri.toString());

                setCallStatus('📞 Llamada entrante...');
                setCallStatusColor('orange');
                setIncomingSession(session);
                setShowIncomingCall(true);

                // Reproducir tono de llamada clásico
                playIncomingRingtone();

                // Configurar eventos de la sesión entrante
                session.on('ended', () => {
                    console.log('Llamada entrante finalizada');
                    stopRingtone();
                    setShowIncomingCall(false);
                    setIncomingSession(null);
                    setCallStatus('🔴 Llamada entrante rechazada o finalizada');
                    setCallStatusColor('gray');
                    setIncomingCallStatus('ended');
                    setSoftphoneSessionDirection('none'); //REVISAR
                });

                session.on('failed', () => {
                    console.log('Llamada entrante fallida');
                    stopRingtone();
                    setShowIncomingCall(false);
                    setIncomingSession(null);
                    setCallStatus('❌ Llamada entrante falló');
                    setCallStatusColor('red');
                    setIncomingCallStatus('failed');
                    setSoftphoneSessionDirection('none'); //REVISAR
                });

                //resto de eventos para actualizar el estado de la llamada
                session.on('peerconnection', () => { setIncomingCallStatus('peerconnection'); });
                session.on('accepted', () => { setIncomingCallStatus('accepted'); });
                session.on('progress', () => { setIncomingCallStatus('progress'); });
                session.on('confirmed', () => { setIncomingCallStatus('confirmed'); });
                session.on('incoming', () => { setIncomingCallStatus('incoming'); })

            }
        });
    }

    const hackHoldAndUnholdAfterCallStart = (session) => {
        // Este hack es para forzar a que el audio se conecte correctamente 
        // en algunos navegadores que tienen problemas con el flujo de medios al inicio de la llamada.
        console.log('Ejecutando hack de hold/unhold para mejorar conexión de audio');
        setTimeout(() => {
            if (session && !session.isEnded()) {
                session.hold();
                setTimeout(() => {
                    if (session && !session.isEnded()) {
                        session.unhold();
                    }
                }, 1000);
            }
        }, 1000);
    }

    const callClient = (destinoCliente, extraHeaders) => {
        if (!uaRef.current) {
            alert('Primero debes registrarte');
            return;
        }
        if (!destinoCliente.trim()) {
            alert('Ingresa un destino');
            return;
        }
        if (sessionClienteRef.current && !sessionClienteRef.current.isEnded()) {
            alert('Ya tienes una sesión activa con el cliente');
            return;
        }

        setCallStatus('📞 Llamando al cliente...');
        setCallStatusColor('orange');

        const options = {
            mediaConstraints: { audio: true, video: false },
            rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
            extraHeaders: extraHeaders || []
        };

        sessionClienteRef.current = uaRef.current.call(destinoCliente, options);

        // Reproducir tono de llamada saliente
        playRingtone();

        sessionClienteRef.current.on('confirmed', () => {
            stopRingtone(); // Detener tono cuando se confirma la llamada
            attachMedia(sessionClienteRef.current);
            setCallStatus('✅ Hablando con el cliente');
            setCallStatusColor('green');
            console.log('Sesión cliente confirmada');
            setOutgoingCallStatus('confirmed');
            actualizarBotones();
            hackHoldAndUnholdAfterCallStart(sessionClienteRef.current); 
        });

        sessionClienteRef.current.on('ended', () => {
            stopRingtone(); // Detener tono si la llamada termina
            setCallStatus('🔴 Llamada con el cliente finalizada');
            setCallStatusColor('gray');
            sessionClienteRef.current = null;
            setOutgoingCallStatus('ended');
            actualizarBotones();
        });

        sessionClienteRef.current.on('failed', (e) => {
            stopRingtone(); // Detener tono si la llamada falla
            setCallStatus('❌ Error en la llamada con el cliente');
            setCallStatusColor('red');
            console.error('Call failed:', e);
            sessionClienteRef.current = null;
            setOutgoingCallStatus('failed');
            actualizarBotones();
        });

        //registra eventos adicionales para actualizar el estado de la llamada
        sessionClienteRef.current.on('peerconnection', () => { setOutgoingCallStatus('peerconnection'); });
        sessionClienteRef.current.on('progress', () => { setOutgoingCallStatus('progress'); });
    };


    const answerIncomingCall = () => {
        if (!incomingSession) return;

        // Detener el tono de llamada
        stopRingtone();

        incomingSession.answer({
            mediaConstraints: { audio: true, video: false },
            rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false }
        });

        incomingSession.on('confirmed', () => {
            attachMedia(incomingSession);
            setCallStatus('✅ Llamada entrante conectada');
            setCallStatusColor('green');
            setShowIncomingCall(false);
            setIncomingCallStatus('confirmed');

            // Asignar la sesión entrante como sessionCliente si no hay una activa
            if (!sessionClienteRef.current || sessionClienteRef.current.isEnded()) {
                sessionClienteRef.current = incomingSession;
                actualizarBotones();
            }

            hackHoldAndUnholdAfterCallStart(incomingSession);
        });

        // incomingSession.on('incoming', () => { setIncomingCallStatus('incoming'); })
        // incomingSession.on('ended', () => { setIncomingCallStatus('ended'); });
        // incomingSession.on('failed', () => { setIncomingCallStatus('failed'); });


        setIncomingSession(null);
    };

    // Rechazar llamada entrante
    const rejectIncomingCall = () => { 
        if (!incomingSession) return;

        // Detener el tono de llamada
        stopRingtone();

        incomingSession.terminate();
        setIncomingSession(null);
        setShowIncomingCall(false);
        setCallStatus('🔴 Llamada entrante rechazada');
        setCallStatusColor('gray');
    };


    const hangUpAllCalls = () => { //colgar todas las llamadas activas
        if (sessionClienteRef.current && !sessionClienteRef.current.isEnded()) {
            sessionClienteRef.current.terminate();
        }
        if (sessionEjecutivoRef.current && !sessionEjecutivoRef.current.isEnded()) {
            sessionEjecutivoRef.current.terminate();
        }

        sessionClienteRef.current = null;
        sessionEjecutivoRef.current = null;
        setCallStatus('🔴 Todas las llamadas terminadas');
        setCallStatusColor('gray');
        console.log('Todas las sesiones terminadas');
        actualizarBotones();
    };

    const hangUpClient = () => {
        if (sessionClienteRef.current && !sessionClienteRef.current.isEnded()) {
            sessionClienteRef.current.terminate();
        }
        sessionClienteRef.current = null;
        setCallStatus('🔴 Llamada cliente terminada');
        setCallStatusColor('gray');
        actualizarBotones();
    };

    const hangUpExecutive = () => { 
        if (sessionEjecutivoRef.current && !sessionEjecutivoRef.current.isEnded()) {
            sessionEjecutivoRef.current.terminate();
        }
        sessionEjecutivoRef.current = null;
        setCallStatus('🔴 Llamada ejecutivo terminada');
        setCallStatusColor('gray');
        actualizarBotones();
    };

    const mute = (target) => {
        if (target === 'client' && sessionClienteRef.current) {
            sessionClienteRef.current.mute({ audio: true });
        } else if (target === 'executive' && sessionEjecutivoRef.current) {
            sessionEjecutivoRef.current.mute({ audio: true });
        }
        setMuted(true);
    }

    const unmute = (target) => {
        if (target === 'client' && sessionClienteRef.current) {
            sessionClienteRef.current.unmute({ audio: true });
        } else if (target === 'executive' && sessionEjecutivoRef.current) {
            sessionEjecutivoRef.current.unmute({ audio: true });
        }
        setMuted(false);
    }

    const hold = (target) => {
        if (target === 'client' && sessionClienteRef.current) {
            sessionClienteRef.current.hold({ audio: true });
        } else if (target === 'executive' && sessionEjecutivoRef.current) {
            sessionEjecutivoRef.current.hold({ audio: true });
        }
        setHolded(true);
    }

    const unhold = (target) => {
        if (target === 'client' && sessionClienteRef.current) {
            sessionClienteRef.current.unhold({ audio: true });
        } else if (target === 'executive' && sessionEjecutivoRef.current) {
            sessionEjecutivoRef.current.unhold({ audio: true });
        }
        setHolded(false);
    }


    // Llamar al ejecutivo
    const callExecutive = (extraHeaders) => {

        if (!uaRef.current) {
            alert('Primero debes registrarte');
            return;
        }
        if (!destinoEjecutivo.trim()) {
            alert('Ingresa un destino');
            return;
        }
        if (!sessionClienteRef.current || sessionClienteRef.current.isEnded()) {
            alert('Primero debes tener una llamada activa con el cliente');
            return;
        }
        if (sessionEjecutivoRef.current && !sessionEjecutivoRef.current.isEnded()) {
            alert('Ya tienes una sesión activa con el ejecutivo');
            return;
        }

        // IMPORTANTE: Poner en HOLD al cliente
        sessionClienteRef.current.hold();
        setCallStatus('⏸️ Cliente en espera. Llamando al ejecutivo...');
        setCallStatusColor('orange');
        console.log('Cliente puesto en hold');
        actualizarBotones();

        const options = {
            mediaConstraints: { audio: true, video: false },
            rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
            extraHeaders: extraHeaders || []
        };

        sessionEjecutivoRef.current = uaRef.current.call(destinoEjecutivo, options);

        // Reproducir tono de llamada saliente
        playRingtone();

        sessionEjecutivoRef.current.on('confirmed', () => {
            stopRingtone(); // Detener tono cuando se confirma la llamada
            attachMedia(sessionEjecutivoRef.current);
            setCallStatus('✅ Hablando con el ejecutivo (cliente en espera)');
            setCallStatusColor('green');
            console.log('Sesión ejecutivo confirmada');
            setExecutiveCallStatus('confirmed');
            actualizarBotones();
            hackHoldAndUnholdAfterCallStart(sessionEjecutivoRef.current); 
        });

        sessionEjecutivoRef.current.on('ended', () => {
            stopRingtone(); // Detener tono si la llamada termina
            setCallStatus('🔴 Llamada con el ejecutivo finalizada');
            setCallStatusColor('gray');
            sessionEjecutivoRef.current = null;
            setExecutiveCallStatus('ended');

            // Si el cliente sigue activo, quitarle el hold
            if (sessionClienteRef.current && !sessionClienteRef.current.isEnded()) {
                sessionClienteRef.current.unhold();
                setCallStatus('✅ De vuelta con el cliente');
                setCallStatusColor('green');
            }
            actualizarBotones();
        });

        sessionEjecutivoRef.current.on('failed', (e) => {
            stopRingtone(); // Detener tono si la llamada falla
            setCallStatus('❌ Error llamando al ejecutivo');
            setCallStatusColor('red');
            console.error('Call to executive failed:', e);
            sessionEjecutivoRef.current = null;
            setExecutiveCallStatus('failed');

            if (sessionClienteRef.current && !sessionClienteRef.current.isEnded()) {
                sessionClienteRef.current.unhold();
                setCallStatus('✅ De vuelta con el cliente');
                setCallStatusColor('green');
            }
            actualizarBotones();
        });
    };

    // Transferir llamada ASISTIDA 
    const transferClientCallToExecutive = () => {
        if (!sessionClienteRef.current || sessionClienteRef.current.isEnded()) {
            alert('No hay sesión activa con el cliente');
            return;
        }
        if (!sessionEjecutivoRef.current || sessionEjecutivoRef.current.isEnded()) {
            alert('No hay sesión activa con el ejecutivo');
            return;
        }

        setCallStatus('🔄 Iniciando transferencia asistida...');
        setCallStatusColor('orange');
        console.log('Iniciando attended transfer');

        const targetUri = sessionEjecutivoRef.current.remote_identity.uri.toString();
        console.log("debug-->", targetUri, sessionClienteRef.current.remote_identity.uri.toString());

        try {
            // CLAVE: replaces hace que sea transferencia ASISTIDA
            sessionClienteRef.current.refer(targetUri, {
                replaces: sessionEjecutivoRef.current,
                extraHeaders: [
                    'Referred-By: ' + sessionClienteRef.current.remote_identity.uri.toString()
                ]
            });

            sessionClienteRef.current.on('refer_succeeded', () => {
                setCallStatus('✅ Transferencia exitosa - Cliente y ejecutivo conectados');
                setCallStatusColor('green');
                console.log('Transfer succeeded');

                // Cerrar ambas sesiones después de confirmar
                setTimeout(() => {
                    if (sessionClienteRef.current && !sessionClienteRef.current.isEnded()) {
                        sessionClienteRef.current.terminate();
                    }
                    if (sessionEjecutivoRef.current && !sessionEjecutivoRef.current.isEnded()) {
                        sessionEjecutivoRef.current.terminate();
                    }
                    sessionClienteRef.current = null;
                    sessionEjecutivoRef.current = null;
                    actualizarBotones();
                }, 1000);
            });

            sessionClienteRef.current.on('refer_failed', (data) => {
                setCallStatus('❌ Transferencia fallida');
                setCallStatusColor('red');
                console.error('Transfer failed:', data);
                actualizarBotones();
            });

        } catch (error) {
            console.error('Error en refer:', error);
            setCallStatus('❌ Error: ' + error.message);
            setCallStatusColor('red');
        }
    };


    const unregister = () => {
        // Finalizar todas las llamadas activas
        stopRingtone();

        if (sessionClienteRef.current && !sessionClienteRef.current.isEnded()) {
            sessionClienteRef.current.terminate();
        }
        if (sessionEjecutivoRef.current && !sessionEjecutivoRef.current.isEnded()) {
            sessionEjecutivoRef.current.terminate();
        }
        sessionClienteRef.current = null;
        sessionEjecutivoRef.current = null;

        // Desregistrar y detener el UA de Asterisk
        if (uaRef.current) {
            uaRef.current.unregister({ all: true });
            uaRef.current.stop();
            uaRef.current = null;
        }

        // Resetear estados
        setIsRegistered(false);
        setSoftphoneConnectionStatus('disconnected');
        setRegStatus('❌ Desconectado');
        setRegStatusColor('gray');
        setCallStatus('');
        setCallStatusColor('gray');
        setOutgoingCallStatus('none');
        setIncomingCallStatus('none');
        setExecutiveCallStatus('none');
        setSoftphoneSessionDirection('none');
        setIncomingSession(null);
        setShowIncomingCall(false);
        setMuted(false);
        setHolded(false);

        console.log('Softphone desregistrado y todas las llamadas finalizadas');
    };

    const actualizarBotones = () => {
        // const clienteActivo = sessionClienteRef.current && !sessionClienteRef.current.isEnded();
        // const ejecutivoActivo = sessionEjecutivoRef.current && !sessionEjecutivoRef.current.isEnded();
        // const clienteEnHold = clienteActivo && sessionClienteRef.current.isOnHold().local;

        // setBtnStates({
        //     llamarEjecutivo: !clienteActivo,
        //     holdCliente: !clienteActivo || clienteEnHold,
        //     unholdCliente: !clienteActivo || !clienteEnHold,
        //     transferir: !clienteActivo || !ejecutivoActivo
        // });
    };

    return (
        <SoftphoneContext.Provider value={
            { softphoneConnectionStatus, softphoneSessionDirection, outgoingCallStatus, incomingCallStatus, register, unregister,
                callClient, hangUpAllCalls, answerIncomingCall, rejectIncomingCall, mute, unmute, hold, unhold, muted, 
                holded, callExecutive, transferClientCallToExecutive, executiveCallStatus, hangUpClient, hangUpExecutive } }>
            {children}
            <audio ref={remoteAudioRef} autoPlay />
        </SoftphoneContext.Provider>
    );
}

// Hook personalizado
export function useSoftphone() {
    const context = useContext(SoftphoneContext);
    if (!context) {
        throw new Error('useSoftphone debe usarse dentro de SoftphoneProvider');
    }
    return context;
}